/**
 * The API's `emailMode`; absent = BUILDER. BUILDER and CUSTOM send alike (subject + `content`, `[[key]]` from
 * `placeholders`); BUILDER also keeps `unlayerSourceData`. MAILEON sends a stored template, no subject or content.
 */
export type EmailMode = 'BUILDER' | 'CUSTOM' | 'MAILEON';

/** Dialog-only: the builder's HTML while another mode is tried. Never saved (`pruneEmailMode`). */
const BUILDER_HTML = 'builderHtml';

export const readEmailMode = (config?: Record<string, any> | null): EmailMode => (
    config?.emailMode === 'CUSTOM' || config?.emailMode === 'MAILEON' ? config.emailMode : 'BUILDER'
);

/** Switching keeps everything, so trying another mode and coming back restores the builder design and its HTML. */
export const applyEmailMode = (config: Record<string, any>, mode: EmailMode): Record<string, any> => {
    const current = readEmailMode(config);
    if (current === mode) return config;

    const next: Record<string, any> = {...config, emailMode: mode};
    if (current === 'BUILDER' && config.unlayerSourceData && config[BUILDER_HTML] === undefined) {
        next[BUILDER_HTML] = config.content ?? '';
    }
    if (mode === 'BUILDER' && config[BUILDER_HTML] !== undefined) {
        next.content = config[BUILDER_HTML];
        delete next[BUILDER_HTML];
    }
    return next;
};

/** Back to the builder would replace HTML edited by hand since leaving it. */
export const builderReplacesEdits = (config: Record<string, any>): boolean => (
    readEmailMode(config) === 'CUSTOM' && config[BUILDER_HTML] !== undefined && config[BUILDER_HTML] !== (config.content ?? '')
);

/** The builder has no design to open, so it would start empty and overwrite this HTML. */
export const builderOverwritesHtml = (config: Record<string, any>): boolean => (
    !config.unlayerSourceData && !!String(config.content ?? '').trim()
);

/** On save: drops what the mode cannot send, so the API is never handed two bodies to choose between. */
export const pruneEmailMode = (config: Record<string, any>): Record<string, any> => {
    const mode = readEmailMode(config);
    const next: Record<string, any> = {...config};
    delete next[BUILDER_HTML];
    if (mode !== 'MAILEON') delete next.maileonTemplateId;
    if (mode !== 'BUILDER') delete next.unlayerSourceData;
    if (mode === 'MAILEON') {
        delete next.subject;
        delete next.content;
    }
    return next;
};
